/**
 * The runway: every dated item on one axis, one lane per type. A 30 Days /
 * 120 Days / 1 Year segment sets the range; the axis has the Today line and
 * the month starts; anything already past sits in the Passed column as a
 * danger badge. Labels flip left past 60% of the range and drop when they
 * would collide (the marker keeps its tooltip).
 */
import dayjs from "dayjs";
import type { FC, ReactNode } from "react";

import { KINDS, type Kind } from "@/components/ui/kind-icon";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Surface } from "@/components/surface";
import { cn } from "@/lib/utils";

import type { RunwayDays } from "../dashboardLayoutStore";
import type { RunwayMark } from "./homeKinds";
import { KindTile } from "./KindTile";
import { dayOffset, monthTicks } from "./runwayModel";

const RANGES: { value: RunwayDays; label: string }[] = [
  { value: 30, label: "30 Days" },
  { value: 120, label: "120 Days" },
  { value: 365, label: "1 Year" }
];

const FLIP_AT = 0.6;
/** Share of the range a label needs before the next one can show. */
const LABEL_GAP = 0.24;
/** Share of the range the "Today, Oct 11" label covers; month labels inside it are hidden. */
const TODAY_LABEL_SHARE = 0.2;

const DOT: Record<Kind, string> = {
  deadline: "bg-kind-deadline",
  expiration: "bg-kind-expiration",
  agent: "bg-kind-agent",
  intake: "bg-kind-intake",
  calendar: "bg-kind-calendar"
};

const markLabel = (m: RunwayMark) => m.label;
const passedLabel = (m: RunwayMark) =>
  `${dayjs(m.date).format("MMM D")} · ${m.title}`;

type Props = {
  marks: RunwayMark[];
  lanes: Kind[];
  days: RunwayDays;
  onDaysChange: (days: RunwayDays) => void;
  /** A quiet line in a lane with nothing on it (or after its bars). */
  notes?: Partial<Record<Kind, ReactNode>>;
  /** Calendar lane: events per day, starting today. */
  bars?: number[];
  onOpen?: (mark: RunwayMark) => void;
  today?: Date;
  className?: string;
};

export const HomeRunway: FC<Props> = ({
  marks,
  lanes,
  days,
  onDaysChange,
  notes = {},
  bars,
  onOpen,
  today = new Date(),
  className
}) => {
  const x = (offset: number) => `${(offset / days) * 100}%`;
  const ticks = monthTicks(today, days);
  const title = days === 365 ? "Next Year" : `Next ${days} Days`;

  const grid = (
    <>
      <span className="absolute inset-y-0 left-0 w-px bg-fg-4" />
      {ticks.map((t) => (
        <span
          key={t.offset}
          className="absolute inset-y-0 w-px bg-line-2"
          style={{ left: x(t.offset) }}
        />
      ))}
    </>
  );

  return (
    <Surface className={className} data-testid="home-runway">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-line-1 pr-2 pl-4">
        <span className="shrink-0 text-base font-medium text-fg">{title}</span>
        <span className="min-w-0 truncate text-sm text-fg-3">
          Every dated item, one lane per type
        </span>
        <span className="flex-1" />
        <Tabs
          value={String(days)}
          onValueChange={(v) => onDaysChange(Number(v) as RunwayDays)}
        >
          <TabsList aria-label="Runway Range">
            {RANGES.map((r) => (
              <TabsTrigger key={r.value} value={String(r.value)}>
                {r.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
      <div className="px-4 pb-1.5">
        <div className="flex h-6.5">
          <span className="w-42 shrink-0" />
          <span className="flex w-36 shrink-0 items-center text-xs text-fg-4">
            Passed
          </span>
          <div className="relative min-w-0 flex-1 text-xs">
            <span className="absolute top-1.5 left-1.5 font-medium whitespace-nowrap text-fg">
              Today, {dayjs(today).format("MMM D")}
            </span>
            {ticks
              .filter((t) => t.offset / days >= TODAY_LABEL_SHARE)
              .map((t) => (
                <span
                  key={t.offset}
                  className="absolute top-1.5 pl-1.5 whitespace-nowrap text-fg-4"
                  style={{ left: x(t.offset) }}
                >
                  {t.label}
                </span>
              ))}
          </div>
        </div>
        {lanes.map((kind) => {
          const laneMarks = marks
            .filter((m) => m.kind === kind)
            .map((m) => ({ ...m, offset: dayOffset(m.date, today) }));
          const passed = laneMarks.filter((m) => m.offset < 0);
          const ahead = laneMarks.filter(
            (m) => m.offset >= 0 && m.offset <= days
          );
          const laneBars = kind === "calendar" ? bars : undefined;
          let lastLabeled = -Infinity;
          let lastFlipped = false;
          return (
            <div
              key={kind}
              className="flex h-8.5 border-t border-line-1"
              data-testid={`runway-lane-${kind}`}
            >
              <span className="flex w-42 shrink-0 items-center gap-2 pr-2">
                <KindTile kind={kind} size="sm" />
                <span className="truncate text-sm text-fg-2">
                  {KINDS[kind].label}
                </span>
              </span>
              <span className="flex w-36 shrink-0 items-center pr-2">
                {passed.length === 1 ? (
                  <button
                    type="button"
                    title={markLabel(passed[0])}
                    onClick={onOpen ? () => onOpen(passed[0]) : undefined}
                    className="min-w-0 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <StatusBadge tone="danger" className="max-w-full">
                      <span className="truncate">{passedLabel(passed[0])}</span>
                    </StatusBadge>
                  </button>
                ) : passed.length > 1 ? (
                  <StatusBadge
                    tone="danger"
                    title={passed.map(markLabel).join("\n")}
                    data-testid={`runway-passed-${kind}`}
                  >
                    {passed.length} Passed
                  </StatusBadge>
                ) : null}
              </span>
              <div className="relative min-w-0 flex-1">
                {grid}
                {ahead.map((m) => {
                  const at = m.offset / days;
                  const flip = at > FLIP_AT;
                  const gap = at - lastLabeled;
                  const showLabel =
                    gap >= (flip && !lastFlipped ? LABEL_GAP * 2 : LABEL_GAP);
                  if (showLabel) {
                    lastLabeled = at;
                    lastFlipped = flip;
                  }
                  return (
                    <button
                      key={m.id}
                      type="button"
                      title={markLabel(m)}
                      aria-label={markLabel(m)}
                      onClick={onOpen ? () => onOpen(m) : undefined}
                      className={cn(
                        "absolute top-1/2 flex -translate-y-1/2 items-center gap-1.5 rounded-xs outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                        flip && "flex-row-reverse"
                      )}
                      style={
                        flip
                          ? { right: `calc(${100 - at * 100}% - 5px)` }
                          : { left: `calc(${x(m.offset)} - 5px)` }
                      }
                    >
                      <span
                        className={cn("size-2.5 rounded-full", DOT[kind])}
                      />
                      {showLabel ? (
                        <span className="max-w-64 truncate text-sm text-fg">
                          {markLabel(m)}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
                {laneBars?.map((n, i) =>
                  i < days ? (
                    <span
                      key={i}
                      className={cn(
                        "absolute bottom-2 -ml-0.5 w-1 rounded-xs",
                        DOT[kind],
                        n === 0 && "opacity-30"
                      )}
                      style={{
                        left: x(i + 0.5),
                        height: `${Math.min(18, 2 + n * 2)}px`
                      }}
                    />
                  ) : null
                )}
                {notes[kind] && ahead.length === 0 ? (
                  <span
                    className="absolute top-1/2 -translate-y-1/2 truncate text-sm whitespace-nowrap text-fg-3"
                    style={{
                      left: laneBars?.length
                        ? `calc(${x(Math.min(days, laneBars.length))} + 12px)`
                        : "10px"
                    }}
                  >
                    {notes[kind]}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </Surface>
  );
};
